import {getEnvironment} from "./themeUtils";
import { t } from "@lingui/core/macro";

const hostnameNoWww = window.location.hostname.replace(/^(www\.)/,"");
const productionMode = getEnvironment("PROD_HOSTNAMES").split(",").includes(hostnameNoWww);

// "/"-separated segments of letters, digits, "-" and "_" only: no "//", no ".", no query, fragment or encoding.
// Every repetition has to end in "/", so the pattern cannot backtrack on long input.
const TRADE_PORTAL_PATH_PATTERN = /^\/(?:[A-Za-z0-9_-]+\/)*[A-Za-z0-9_-]*$/;

// subdomain text can only include lowercase letters, digits and hyphens
const SUBDOMAIN_PATTERN = /^[a-z0-9-]+$/;

// Accepts only a whitelisted origin, since it is prepended to every trade portal redirect.
// "https://*.example.com" allows https://trade.example.com, but not https://example.com or https://a.b.example.com.
// Everything around the "*" has to match exactly, which rejects another scheme, host or port, a path, query, fragment or credentials.
function getTradePortalOrigin(baseUrl) {
    const allowedOrigins = (getEnvironment("TRADE_PORTAL_ALLOWED_ORIGINS") || "").split(",").map(origin => origin.trim()).filter(Boolean);
    const origin = baseUrl.replace(/\/$/, "");
    const isAllowed = allowedOrigins.some(allowedOrigin => {
        if (!allowedOrigin.includes("*")) {
            return origin === allowedOrigin;
        }
        // "https://*.example.com" gives start "https://" and end ".example.com"; whatever sits between them is the subdomain
        const [start, end] = allowedOrigin.split("*");
        const subdomain = origin.slice(start.length, origin.length - end.length);
        return origin.startsWith(start) && origin.endsWith(end) && SUBDOMAIN_PATTERN.test(subdomain);
    });
    return isAllowed ? origin : null;
}

// Reads the trade portal redirect targets from the session login link. Returns null for a non-trade session
// (no baseUrl) or when any supplied value fails validation, so the session keeps the non-trade exits.
export function getTradePortalUrls(searchParams) {
    const baseUrl = searchParams.get("baseUrl");
    if (!baseUrl) {
        return null;
    }

    const origin = getTradePortalOrigin(baseUrl);
    const homePath = searchParams.get("homePath") || "/";
    const bookingPath = searchParams.get("bookingPath") || homePath;
    const logoutPath = searchParams.get("logoutPath") || "";

    const pathsValid = [homePath, bookingPath, logoutPath].every(path => path === "" || TRADE_PORTAL_PATH_PATTERN.test(path));
    if (!origin || !pathsValid) {
        return null;
    }

    return {
        homeUrl: origin + homePath,
        bookingUrl: origin + bookingPath,
        logoutUrl: logoutPath ? origin + logoutPath : "",
    };
}

export function getHubUrl(includeSlash = true) {
    return productionMode ?
        `${getEnvironment("PROD_HUB_URL")}/v1${includeSlash ? "/" : ""}`  :
        `${getEnvironment("DEV_HUB_URL")}/v1${includeSlash ? "/" : ""}`;
}

export function getHubBaseUrl(includeSlash = true) {
    return productionMode ?
        `${getEnvironment("PROD_HUB_URL")}${includeSlash ? "/" : ""}` :
        `${getEnvironment("DEV_HUB_URL")}${includeSlash ? "/" : ""}`;
}

export function isDevelopmentMode() {
    return window.location.hostname === "localhost" || window.location.hostname === "127.0.0.1";
}

export function useExcursionTags() {
    return getEnvironment("USE_EXCURSION_TAGS") === "true";
}

export function showExpiredTabMessage() {
    return getEnvironment("SHOW_EXPIRED_TAB_MESSAGE") === "true";
}

export function skipPayment() {
    return getEnvironment("SKIP_PAYMENT") === "true";
}

// TODO: Remove env variables once the /prebooking/tac/config API supports per-client/theme config.
export function areRestaurantsEnabled() {
    return getEnvironment("ENABLE_RESTAURANTS") === "true";
}

export function areActivitiesEnabled() {
    return getEnvironment("ENABLE_ACTIVITIES") === "true";
}

export function getExternalUrl() {
    return productionMode ?
        getEnvironment("PROD_EXTERNAL_URL") :
        getEnvironment("DEV_EXTERNAL_URL");
}
export function getPostLogoutUrl(tradePortalLogoutUrl = "") {
    if (tradePortalLogoutUrl) {
        return tradePortalLogoutUrl;
    }
    const externalUrl = getExternalUrl();
    return externalUrl ? `${externalUrl}/${t({id: "logout_external"})}` : "/login";
}

export function getExternalDashboardUrl() {
    const externalUrl = getExternalUrl();
    return externalUrl ? `${externalUrl}/${t({id: "dashboard_external"})}` : false;
}


export function clearLocalStorageOnLogout() {
    return getEnvironment("CLEAR_STORAGE_ON_LOGOUT") === "true";
}

export function useGoogleAnalytics() {
    return getEnvironment("USE_GOOGLE_ANALYTICS") === "true";
}

export function getGAID() {
    return getEnvironment("GA_ID");
}

export function getAdyenEnvironment() {
    return productionMode ? "live" : "test";
}